// Thresholds e decisão do FAILOVER DA ABERTURA (perf/tv-live-zap-failover, item 2).
//
// PROBLEMA (device board): canal com a fonte primária MORTA (aceita a conexão
// mas não entrega um byte) ficou ~10s preso em "Carregando" — o <video> parado
// em HAVE_NOTHING (readyState 0 / videoWidth 0, o "w0/rs0" medido) — antes do
// auto-switch pular pra fonte 2 (1º frame só aos ~13s). O único relógio que
// governava esse stall INICIAL era o watchdog tier-aware de 10-20s.
//
// FIX — failover da abertura em DUAS FASES:
//
//  FASE 1 ("nada chegou"): uma origem que não entrega NADA em ~4,5s não vai
//  entregar. Isso NÃO melhora com CPU melhor — é a origem, não o device. Falha
//  RÁPIDO. O gate por readyState é o que torna seguro encurtar: só falha se o
//  <video> ainda está em HAVE_NOTHING. Fonte lenta-mas-viva (vlow+3G, comum) já
//  tem metadata (readyState >= HAVE_METADATA) e é POUPADA.
//
//  FASE 2 ("veio metadata mas travou"): aí o tier importa de verdade (vlow
//  decodifica devagar), então mantém o teto conservador de 10-20s como backstop.
//  É `initialLoadFailoverMs` — comportamento pré-existente, byte-idêntico.
//
// NÃO mexe no mid-stream: o stall DEPOIS do 1º frame (hasPlayed) segue no
// watchdog conservador de 8-15s (freeze/buffering) — este módulo só decide a
// ABERTURA.

/** `HTMLMediaElement.readyState` — nada carregado ainda (sem metadata). */
export const HAVE_NOTHING = 0

export interface OpenFailoverInput {
  /** A carga ainda é a ativa? (false = superada por um zap novo → não falha). */
  isActiveLoad: boolean
  /** Já houve 1º frame nesta cadeia? (true = mid-stream, não é abertura). */
  hasPlayed: boolean
  /** `video.readyState` no instante do watchdog (0 = HAVE_NOTHING = nada chegou). */
  readyState: number
}

/**
 * Decide se a FASE 1 (nada chegou) deve rotacionar de fonte na abertura.
 *
 * true SÓ quando: a carga ainda é a ativa, nunca tocou, e o <video> segue em
 * HAVE_NOTHING (origem entregou zero byte). Qualquer sinal de vida
 * (readyState >= HAVE_METADATA) => false: a origem está viva, só lenta — deixa o
 * backstop tier-aware (FASE 2) decidir, sem falso-switch em device fraco.
 */
export function shouldFailoverOnOpen(input: OpenFailoverInput): boolean {
  if (!input.isActiveLoad) return false
  if (input.hasPlayed) return false
  return input.readyState <= HAVE_NOTHING
}

/**
 * FASE 1 — teto (ms) do "nada chegou". Tier-aware por segurança: em vlow o
 * próprio demux do mpegts na main thread pode atrasar o 1º append que sobe o
 * readyState 0→1 mesmo com bytes chegando, então dá folga pra não cortar fonte
 * boa. Ainda MUITO menor que a FASE 2.
 */
export function openNoDataFailoverMs(tier: string): number {
  if (tier === 'vlow') return 6_500
  if (tier === 'low') return 5_500
  return 4_500 // mid+ : alvo do board (~4-5s)
}

/**
 * FASE 2 — teto (ms) do backstop conservador da abertura ("veio metadata mas
 * travou"). Valores pré-existentes (eram inline no LivePlayerProvider): vlow/low
 * podem legitimamente levar 8-14s pro 1º playback (HLS parse + key + segment).
 */
export function initialLoadFailoverMs(tier: string): number {
  if (tier === 'vlow') return 20_000
  if (tier === 'low') return 15_000
  return 10_000
}
